import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { LayoutChangeEvent, StyleProp, ViewStyle } from 'react-native';
import Animated, {
    Easing,
    SharedValue,
    cancelAnimation,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { Box } from './lib/components/Box';

export const ReanimatedAccordion = ({
    expanded,
    children,
    style,
    duration = 200,
    contentHeight,
}: {
    expanded: SharedValue<boolean>;
    children: ReactNode;
    style?: StyleProp<ViewStyle>;
    duration?: number;
    contentHeight?: number;
}) => {
    const height = useSharedValue(contentHeight ?? 0);
    const progress = useSharedValue(0);
    const [shouldRender, setShouldRender] = useState(expanded.value);

    useEffect(() => {
        if (contentHeight) {
            // since onLayout is disabled if we pass contentHeight
            // we need to keep the shared value updated manually
            height.value = contentHeight;
        }
    }, [contentHeight, height]);

    useAnimatedReaction(
        () => expanded.value,
        (current, previous) => {
            if (current !== previous) {
                cancelAnimation(progress);

                if (current) {
                    // render children before starting the expanding animation
                    runOnJS(setShouldRender)(true);
                }

                progress.value = withTiming(
                    current ? 1 : 0,
                    {
                        duration,
                        easing: Easing.inOut(Easing.ease),
                    },
                    isFinished => {
                        if (isFinished && !current) {
                            // don't render children after collapsing animation is done
                            runOnJS(setShouldRender)(false);
                        }
                    }
                );
            }
        }
    );

    const bodyStyle = useAnimatedStyle(() => ({
        height: height.value * progress.value,
        overflow: 'hidden',
    }));

    const handleLayout = useCallback(
        (e: LayoutChangeEvent) => {
            height.value = e.nativeEvent.layout.height;
        },
        [height]
    );

    return (
        <Animated.View style={[bodyStyle, style]}>
            {shouldRender ? (
                <Box onLayout={!contentHeight ? handleLayout : undefined} position={'absolute'} width={'100%'}>
                    {children}
                </Box>
            ) : null}
        </Animated.View>
    );
};
